/** Etiquetas (SPEC-OPP-050).
 *
 * Duas famílias de chamada, e a separação é a mesma do servidor: o vocabulário
 * (`/tags`) é administrado por quem organiza o funil; marcar e desmarcar
 * (`/opportunities/{id}/tags/{tag_id}`) é de quem vende.
 */

import { http } from "@/lib/http";
import type { components } from "@/lib/api-types";

export type CorDaEtiqueta = "cinza" | "azul" | "verde" | "ambar" | "carmim";

export const CORES: { valor: CorDaEtiqueta; rotulo: string }[] = [
  { valor: "cinza", rotulo: "Cinza" },
  { valor: "azul", rotulo: "Azul" },
  { valor: "verde", rotulo: "Verde" },
  { valor: "ambar", rotulo: "Âmbar" },
  { valor: "carmim", rotulo: "Carmim" },
];

/** As classes de cada cor, escritas por extenso.
 *
 * Nada de `bg-${cor}`: o Tailwind varre o código à procura de classes
 * literais, e uma classe montada em tempo de execução simplesmente não entra
 * no CSS — sem erro nenhum, só o chip sem cor.
 */
export const CLASSE_DA_COR: Record<CorDaEtiqueta, string> = {
  cinza: "bg-canvas text-texto-tenue",
  azul: "bg-acao/10 text-acao",
  verde: "bg-musgo/10 text-musgo",
  ambar: "bg-ambar/10 text-ambar",
  carmim: "bg-carmim/10 text-carmim",
};

/** `color` é `string` no contrato (a saída não usa `Literal`); o front
 * sempre assumiu uma das cinco cores de `CorDaEtiqueta`, que são as únicas
 * que ele próprio grava. */
export type Etiqueta = Omit<components["schemas"]["EtiquetaNaListaResponse"], "color"> & {
  color: CorDaEtiqueta;
};

export async function listarEtiquetas(): Promise<Etiqueta[]> {
  const resposta = await http.get<components["schemas"]["ListaDeEtiquetasResponse"]>("/tags");
  return resposta.tags as Etiqueta[];
}

/** `POST /tags` e `PATCH /tags/{id}` devolvem `EtiquetaResponse` — **sem**
 * `usage_count`, que só existe em `EtiquetaNaListaResponse` (a etiqueta como
 * a listagem a devolve). Continuam tipados como `Etiqueta` porque nada lê o
 * retorno destas duas chamadas: `useGestaoDeEtiquetas` só invalida a query e
 * relê a lista de verdade (ver relatório da migração). */
export async function criarEtiqueta(name: string, color: CorDaEtiqueta): Promise<Etiqueta> {
  return http.post<Etiqueta>("/tags", { name, color });
}

export async function atualizarEtiqueta(
  id: string,
  mudancas: { name?: string; color?: CorDaEtiqueta },
): Promise<Etiqueta> {
  return http.patch<Etiqueta>(`/tags/${encodeURIComponent(id)}`, mudancas);
}

export async function apagarEtiqueta(id: string): Promise<void> {
  await http.delete<undefined>(`/tags/${encodeURIComponent(id)}`);
}

/** `PUT`, e não `POST`: marcar duas vezes é a mesma marcação. */
export async function marcar(oportunidade: string, etiqueta: string): Promise<void> {
  await http.put<undefined>(
    `/opportunities/${encodeURIComponent(oportunidade)}/tags/${encodeURIComponent(etiqueta)}`,
  );
}

export async function desmarcar(oportunidade: string, etiqueta: string): Promise<void> {
  await http.delete<undefined>(
    `/opportunities/${encodeURIComponent(oportunidade)}/tags/${encodeURIComponent(etiqueta)}`,
  );
}
